import { useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';
import { useApprovalPassportSearch } from '@/hooks/apiHooks';
import type { Passport } from '@/types/api';
import { imgUrlChk } from '@/utils/function';
import { MIN_KEYWORD_LENGTH, isKeywordSearchable } from '../constants';
import type { Referrer, UseUserSearch } from '../types';

export type { UseUserSearch } from '../types';

const DEBOUNCE_MS = 500;
const LIST_SIZE = 50;

const toReferrer = (p: Passport): Referrer => {
  const normalizedImage = imgUrlChk(p.detail.profileImageUrl);
  return {
    id: p.passportId,
    name: p.detail.name,
    positionName: p.detail.positionName,
    departmentName: p.detail.departmentName,
    divisionName: p.detail.divisionName,
    organizationDisplayName: p.detail.organizationDisplayName,
    profileImageUrl: normalizedImage || undefined,
  };
};

export const useUserSearch: UseUserSearch = (keyword) => {
  // useApprovalPassportSearch 의 최소 길이 게이트는 trim 전 길이 기준 — 공백뿐인 키워드로 호출되지 않게 미리 trim
  const trimmed = keyword.trim();
  const debounced = useDebounce(trimmed, DEBOUNCE_MS);

  // 전자결재 전용 노출 설정 기준 검색 — 파견직도 결재자·참조자로 지정할 수 있어야 한다 (PORTAL-14029)
  const { data, isLoading, error } = useApprovalPassportSearch(
    // 재직자만 조회 — 퇴사(비활성) 프로필 제외
    { keyword: debounced, listSize: LIST_SIZE, activated: 1 },
    MIN_KEYWORD_LENGTH,
  );

  const referrers = useMemo<Referrer[]>(
    () => (data?.data ?? []).map(toReferrer),
    [data],
  );

  // 디바운스 대기 구간은 SWR 키가 아직 이전 키워드 — 로딩으로 안 보면 결과 0건으로 오인됨
  const isDebouncePending =
    trimmed !== debounced && isKeywordSearchable(trimmed);

  return { data: referrers, isLoading: isLoading || isDebouncePending, error };
};
